import React, { useRef, useState } from 'react';
import { FiX } from '../../../icons';
import { toast } from 'react-toastify';
import axios from 'axios';
import apiConfig from '../config/apiConfig.js';
import IconCamera from '../../../images/icon-camera.png';
import UploadIcon from '../../../images/profile-dp.svg';
import ImageCropModal from './ImageCropModal';

interface ProfilePhotoUploadProps {
  userProfile: {
    profileImage: string;
    fullName: string;
    firstName: string;
    lastName: string;
  };
  onProfileUpdate: (imageUrl: string) => void;
  getInitials: () => string;
}

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  userProfile,
  onProfileUpdate,
  getInitials
}) => {
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isImageLoading, setIsImageLoading] = useState<boolean>(false);
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChangeProfilePhoto = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }

      // Create a preview URL for the cropper
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImageSrc(reader.result as string);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);

      // Reset file input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleCropComplete = async (croppedImageBlob: Blob): Promise<void> => {
    try {
      setIsUploading(true);
      setIsImageLoading(true);

      const authToken = localStorage.getItem("authToken");

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("avatar", croppedImageBlob, "profile-photo.jpg");

      const response = await axios.put(
        `${apiConfig.apiBaseURL}/update`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
        }
      );

      if (response.data) {
        // Get the image URL from response - API returns profileUrl in data object
        const imageUrl =
          response.data.data?.profileUrl ||
          response.data.profileUrl ||
          response.data.avatar ||
          response.data.profileImage ||
          URL.createObjectURL(croppedImageBlob);

        setProfileImage(imageUrl);
        setImageFile(new File([croppedImageBlob], "profile-photo.jpg"));

        // Update parent component
        onProfileUpdate(imageUrl);

        toast.success("Profile photo updated successfully!");

        // Emit profile update event for Header to listen
        window.dispatchEvent(new Event('profileUpdated'));
      }
    } catch (error) {
      console.error("Error updating profile image:", error);
      if (axios.isAxiosError(error)) {
        console.error("Request details:", {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });
      }

      toast.error("Failed to update profile photo. Please try again.");
      setIsImageLoading(false);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClearImage = async (): Promise<void> => {
    try {
      const authToken = localStorage.getItem("authToken");

      const response = await axios.put(
        `${apiConfig.apiBaseURL}/update`,
        {
          avatar: "",
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
        }
      );


      if (response.data) {
        setProfileImage(null);
        setImageFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Update parent component
        onProfileUpdate("");

        toast.success("Profile photo removed successfully!");

        // Emit profile update event for Header to listen
        window.dispatchEvent(new Event('profileUpdated'));
      }
    } catch (error) {
      console.error("Error removing profile image:", error);
      if (axios.isAxiosError(error)) {
        console.error("Request details:", {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });
      }

      toast.error("Failed to remove profile photo. Please try again.");
    }
  };

  return (
    <>
      <div className="profile-photo-wrapper mb-0">
        <div
          style={{
            position: "relative",
            width: 120,
            height: 120,
            margin: "10px auto 30px",
          }}
        >
          {userProfile.profileImage &&
          userProfile.profileImage !== UploadIcon ? (
            <img
              src={userProfile.profileImage}
              alt={userProfile.fullName}
              style={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #d0d0d0",
                cursor: isUploading ? "not-allowed" : "pointer",
                opacity: isImageLoading ? 0.6 : 1,
              }}
              onClick={!isUploading ? handleChangeProfilePhoto : undefined}
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
            />
          ) : (
            <div
              className="initials-avatar"
              style={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                background:
                  "linear-gradient(135deg, #3957A0 0%, #D7A360 50%, #99CAEB 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "36px",
                fontWeight: "bold",
                border: "2px solid #d0d0d0",
                cursor: isUploading ? "not-allowed" : "pointer",
                opacity: isUploading ? 0.6 : 1,
              }}
              onClick={!isUploading ? handleChangeProfilePhoto : undefined}
            >
              {getInitials()}
            </div>
          )}

          {/* Loading Overlay - Show during upload OR while image is loading */}
          {(isUploading || isImageLoading) && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <div
                className="spinner-border text-light"
                role="status"
                style={{ width: "40px", height: "40px" }}
              >
                <span className="visually-hidden">
                  {isUploading ? "Uploading..." : "Loading..."}
                </span>
              </div>
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageChange}
            style={{ display: "none" }}
            disabled={isUploading}
          />

          <button
            type="button"
            className="plus-bttn"
            onClick={handleChangeProfilePhoto}
            disabled={isUploading}
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              backgroundColor: "#677289",
              border: "none",
              borderRadius: "50%",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: isUploading ? "not-allowed" : "pointer",
              color: "#fff",
              opacity: isUploading ? 0.6 : 1,
            }}
            title="Upload Photo"
          >
            <img src={IconCamera} alt="Upload Icon" />
          </button>
        </div>
      </div>

      {/* Image Crop Modal */}
      <ImageCropModal
        show={showCropModal}
        imageSrc={selectedImageSrc}
        onHide={() => setShowCropModal(false)}
        onCropComplete={handleCropComplete}
      />
    </>
  );
};

export default ProfilePhotoUpload;